import { useRef, useState, type DragEvent, type KeyboardEvent } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { DateTime } from 'luxon';
import { Download, FileSpreadsheet, UploadCloud } from 'lucide-react';
import { AnimatedNumber } from '@/components/orders-drilldown/animated-number';
import { Button } from '@/components/ui/button';
import { useI18nContext } from '@/i18n/i18n-react';
import { downloadCsvFile } from '@/lib/campaign-import/download-csv-file';
import {
  readCsvFile,
  type CsvDataRow,
  type CsvReadFailure,
} from '@/lib/campaign-import/read-csv-file';
import {
  buildImportTemplateCsv,
  type ImportTemplateLabels,
} from '@/lib/campaign-import/template-csv';
import { cn } from '@/lib/utils';

export interface LoadedCsv {
  fileName: string;
  headers: string[];
  rows: CsvDataRow[];
}

export interface FileStepProps {
  storeWebsiteDomain: string;
  onFileLoaded: (loaded: LoadedCsv) => void;
  /** How long the row count stays on screen before moving on. */
  settleMs?: number;
}

type Phase =
  | { kind: 'idle' }
  | { kind: 'reading'; fileName: string; rowCount: number | null }
  | { kind: 'error'; reason: CsvReadFailure };

const RING_RADIUS = 34;

function useFileStepCopy() {
  const { LL } = useI18nContext();

  const errors: Record<CsvReadFailure, string> = {
    notCsv: LL.campaignImport.file.errors.notCsv(),
    tooLarge: LL.campaignImport.file.errors.tooLarge(),
    empty: LL.campaignImport.file.errors.empty(),
    noHeader: LL.campaignImport.file.errors.noHeader(),
    noDataRows: LL.campaignImport.file.errors.noDataRows(),
    tooManyRows: LL.campaignImport.file.errors.tooManyRows(),
    malformed: LL.campaignImport.file.errors.malformed(),
  };

  const template: ImportTemplateLabels = {
    headers: {
      name: LL.campaignImport.template.headers.name(),
      launchDate: LL.campaignImport.template.headers.launchDate(),
      launchTime: LL.campaignImport.template.headers.launchTime(),
      notificationTitle: LL.campaignImport.template.headers.notificationTitle(),
      notificationBody: LL.campaignImport.template.headers.notificationBody(),
      notificationLink: LL.campaignImport.template.headers.notificationLink(),
      utmCampaign: LL.campaignImport.template.headers.utmCampaign(),
      labels: LL.campaignImport.template.headers.labels(),
    },
    examples: [
      {
        name: LL.campaignImport.template.examples.first.name(),
        notificationTitle: LL.campaignImport.template.examples.first.title(),
        notificationBody: LL.campaignImport.template.examples.first.body(),
        utmCampaign: LL.campaignImport.template.examples.first.utmCampaign(),
        labels: LL.campaignImport.template.examples.first.labels(),
      },
      {
        name: LL.campaignImport.template.examples.second.name(),
        notificationTitle: LL.campaignImport.template.examples.second.title(),
        notificationBody: LL.campaignImport.template.examples.second.body(),
        utmCampaign: LL.campaignImport.template.examples.second.utmCampaign(),
        labels: LL.campaignImport.template.examples.second.labels(),
      },
    ],
  };

  return { errors, template };
}

function ReadingRing({ ready }: { ready: boolean }) {
  const reduceMotion = useReducedMotion();
  const circumference = 2 * Math.PI * RING_RADIUS;

  return (
    <svg
      viewBox="0 0 80 80"
      className="size-20"
      aria-hidden
      data-slot="reading-ring"
    >
      <circle
        cx="40"
        cy="40"
        r={RING_RADIUS}
        fill="none"
        stroke="rgba(255,255,255,0.12)"
        strokeWidth="4"
      />
      <motion.circle
        cx="40"
        cy="40"
        r={RING_RADIUS}
        fill="none"
        stroke="#155DFC"
        strokeWidth="4"
        strokeLinecap="round"
        strokeDasharray={circumference}
        transform="rotate(-90 40 40)"
        initial={{ strokeDashoffset: circumference * 0.75 }}
        animate={{ strokeDashoffset: ready ? 0 : circumference * 0.75 }}
        transition={{
          duration: reduceMotion ? 0 : 0.9,
          ease: [0.16, 1, 0.3, 1],
        }}
      />
    </svg>
  );
}

export function FileStep({
  storeWebsiteDomain,
  onFileLoaded,
  settleMs = 1100,
}: FileStepProps) {
  const { LL } = useI18nContext();
  const reduceMotion = useReducedMotion();
  const { errors, template } = useFileStepCopy();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });

  const isReading = phase.kind === 'reading';
  const openPicker = () => {
    if (!isReading) inputRef.current?.click();
  };

  const handleFile = async (file: File) => {
    setPhase({ kind: 'reading', fileName: file.name, rowCount: null });
    const result = await readCsvFile(file);
    if (!result.ok) {
      setPhase({ kind: 'error', reason: result.reason });
      return;
    }
    setPhase({
      kind: 'reading',
      fileName: file.name,
      rowCount: result.rows.length,
    });
    await new Promise((resolve) =>
      setTimeout(resolve, reduceMotion ? Math.min(settleMs, 300) : settleMs)
    );
    onFileLoaded({
      fileName: file.name,
      headers: result.headers,
      rows: result.rows,
    });
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files[0];
    if (file && !isReading) void handleFile(file);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openPicker();
    }
  };

  const downloadTemplate = () => {
    downloadCsvFile(
      buildImportTemplateCsv({
        storeWebsiteDomain,
        now: DateTime.now(),
        labels: template,
      }),
      LL.campaignImport.template.fileName()
    );
  };

  return (
    <div className="relative space-y-4">
      <input
        ref={inputRef}
        type="file"
        accept=".csv,text/csv"
        className="hidden"
        tabIndex={-1}
        aria-hidden
        data-testid="csv-file-input"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file) void handleFile(file);
        }}
      />

      <motion.div
        role="button"
        tabIndex={0}
        aria-label={LL.campaignImport.file.dropAriaLabel()}
        aria-disabled={isReading}
        data-state={isReading ? 'reading' : isDragging ? 'dragging' : 'idle'}
        onClick={openPicker}
        onKeyDown={handleKeyDown}
        onDragOver={(event) => {
          event.preventDefault();
          if (!isReading) setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        animate={{ scale: isDragging && !reduceMotion ? 1.015 : 1 }}
        transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
        className={cn(
          'flex min-h-64 cursor-pointer flex-col items-center justify-center gap-4 rounded-xl border-2 border-dashed px-6 py-10 text-center outline-none transition-[border-color,background-color,box-shadow] duration-300',
          'focus-visible:ring-4 focus-visible:ring-[#155DFC]/50',
          isDragging
            ? 'border-[#155DFC] bg-[#155DFC]/15 shadow-[0_0_60px_-10px_#155DFC]'
            : 'border-white/25 bg-white/[0.03] hover:border-white/45',
          isReading && 'cursor-default'
        )}
      >
        {phase.kind === 'reading' ? (
          <>
            <ReadingRing ready={phase.rowCount !== null} />
            <div className="space-y-1" role="status" aria-live="polite">
              <p className="flex items-center justify-center gap-2 font-semibold">
                <FileSpreadsheet
                  className="size-4 text-[#7FA6FF]"
                  aria-hidden
                />
                {LL.campaignImport.file.reading({ fileName: phase.fileName })}
              </p>
              {phase.rowCount !== null ? (
                <p className="text-sm text-white/70">
                  <AnimatedNumber
                    value={phase.rowCount}
                    format={(value) =>
                      LL.campaignImport.file.rowsFound({
                        count: Math.round(value),
                      })
                    }
                  />
                </p>
              ) : null}
            </div>
          </>
        ) : (
          <>
            <span
              className={cn(
                'flex size-14 items-center justify-center rounded-full border transition-colors duration-300',
                isDragging
                  ? 'border-[#155DFC] bg-[#155DFC]/25'
                  : 'border-white/20 bg-white/5'
              )}
            >
              <UploadCloud className="size-6" aria-hidden strokeWidth={1.5} />
            </span>
            <div className="space-y-1">
              <p className="text-lg font-semibold">
                {isDragging
                  ? LL.campaignImport.file.dropActive()
                  : LL.campaignImport.file.dropTitle()}
              </p>
              <p className="text-sm text-white/65">
                {LL.campaignImport.file.dropHint()}
              </p>
            </div>
          </>
        )}
      </motion.div>

      {phase.kind === 'error' ? (
        <p
          role="alert"
          className="rounded-lg border border-red-400/30 bg-red-400/10 px-4 py-3 text-sm text-red-100"
        >
          {errors[phase.reason]}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <Button
          type="button"
          variant="outline"
          onClick={downloadTemplate}
          className="border-white/25 bg-transparent text-white hover:bg-white/10 hover:text-white"
        >
          <Download aria-hidden />
          {LL.campaignImport.file.templateButton()}
        </Button>
        <p className="text-sm text-white/60">
          {LL.campaignImport.file.templateHint()}
        </p>
      </div>
    </div>
  );
}
